import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { DistrictLookup } from '../../shared-components/lookup/lookup.model';
import { SecurityService } from '../../shared-components/security/security.service';
import { System } from '../../shared-components/System';
import { YearService } from '../../shared-components/year/year.service';
import { FamilyAddress } from './detailed-registration/address-registration/family-address.model';
import { ContactWorkInfo } from './detailed-registration/household-contacts-registration/contact-work-information/contact-work-info.model';
import { FamilyContact } from './detailed-registration/household-contacts-registration/family-contacts.model';
import { HouseholdFamilyDocument } from './detailed-registration/household-registration/household-registration.model';
import { AddressChangeLog } from './modals/address-change-log/address-change-log.model';
import { RegistrationUnsavedChangesDialogComponent } from './modals/registration-unsaved-changes-dialog/registration-unsaved-changes-dialog.component';
import { StudentLeavesWith } from './modals/student-leaves-with/student-leaves-with.model';
import { StudentReleaseAlert } from './modals/student-release-alerts/student-release-alert.model';
import { AddressBlock } from './models/address-block.model';
import { EnrollmentCalendar } from './models/enrollment-calendar.model';
import { EnrollmentCourse } from './models/enrollment-course.model';
import { EnrollmentHomeroomTeacher } from './models/enrollment-homeroom-teacher.model';
import { QuickRegistrationParent1Info } from './models/quick-registration-parent1-info.model';
import { QuickRegistration } from './models/quick-registration.model';
import { RegistrationDuplicateParentEmail } from './models/registration-duplicate-parent-email.model';
import { RegistrationDuplicatePhoneNumber } from './models/registration-duplicate-phone-number.model';
import { RegistrationLookups } from './models/registration-lookups.model';
import { RegistrationSettings } from './models/registration-settings.model';
import { RegistrationStudentMatchOptions } from './models/registration-student-match-options.model';
import { RegistrationStudent } from './models/registration-student.model';
import { StudentEnrollmentDetail } from './models/student-enrollment-detail.model';
import { StudentEnrollment } from './models/student-enrollment.model';
import { FamilyRegistrationDetails } from './registration-search/registration-family-details/registration-family-details.model'; // TODO: Rename the file
import { RegistrationFamilySearch } from './registration-search/registration-family.model';
import { RegistrationSearchCriteria } from './registration-search/registration-search-critera.model';

@Injectable()
export class RegistrationEnrollmentService {

    registrationSettings: RegistrationSettings;
    registrationStudentMatchOptions: RegistrationStudentMatchOptions;
    registrationLookups: RegistrationLookups;
    familyRegistrationDetails: FamilyRegistrationDetails;
    selectedStep = 0;
    selectedOtherContactTab = 'Nonresident Parents';
    newEmergencyContactTrigger = false;
    studentsLeaveWith: StudentLeavesWith[];
    districtLookup: DistrictLookup[] = [];
    registrationSearchParams: RegistrationSearchParams = new RegistrationSearchParams();
    expandAllNonresident = false;
    unsavedChanges = false;
    errorsExist = false;

    viewReleaseAlertsPrivilege = false;
    createReleaseAlertsPrivilege = false;
    editDeleteReleaseAlertsPrivilege = false;
    maintainFamilyDataPermission = false;
    freeReducedMealPermission = false;
    studentDocumentsPermission = false;
    addStudentPermission = false;
    viewStudentPortalInfoPermission = false;
    editStudentPortalInfoPermission = false;
    changeStudentIDPermission = false;
    idLessThanPermission = false;
    withdrawEnrollmentPermission = false;
    deleteHouseholdPermission = false;

    editStudentId: number;
    editContactId: number;

    observableGuard = new Subject<any>();
    observableGuardStream$ = this.observableGuard.asObservable();

    observableSwitchTab = new Subject<string>();
    observableSwitchTabStream$ = this.observableSwitchTab.asObservable();

    constructor(
        private http: HttpClient,
        private yearService: YearService,
        private securityService: SecurityService,
        private dialog: MatDialog
    ) {
        this.maintainFamilyDataPermission = this.securityService.IsAllPermissionCodeAssigned('SD_MFD');
        this.viewReleaseAlertsPrivilege = this.securityService.IsAllPermissionCodeAssigned('SD_VSRA');
        this.createReleaseAlertsPrivilege = this.securityService.IsAllPermissionCodeAssigned('SD_ASRA');
        this.editDeleteReleaseAlertsPrivilege = this.securityService.IsAllPermissionCodeAssigned('SD_MSRA');
        this.freeReducedMealPermission = this.securityService.IsAllPermissionCodeAssigned('LA_VFRMA') || this.securityService.IsAllPermissionCodeAssigned('LA_MFRMA');
        this.studentDocumentsPermission = this.securityService.IsAllPermissionCodeAssigned('DSF_MADFSF');
        this.addStudentPermission = this.securityService.IsAllPermissionCodeAssigned('SD_ASTF');
        this.viewStudentPortalInfoPermission = this.securityService.IsAllPermissionCodeAssigned('SD_VSPLIAP');
        this.editStudentPortalInfoPermission = this.securityService.IsAllPermissionCodeAssigned('SD_MSPLIAP');
        this.changeStudentIDPermission = this.securityService.IsAllPermissionCodeAssigned('SD_CSI');
        this.idLessThanPermission = this.securityService.IsAllPermissionCodeAssigned('SD_ESILDM');
        this.withdrawEnrollmentPermission = this.securityService.IsAllPermissionCodeAssigned('SD_WS');
        this.deleteHouseholdPermission = this.securityService.IsAllPermissionCodeAssigned('SD_DH');
    }

    getRegistrationSettings(): Observable<RegistrationSettings> {
        return this.http.get<RegistrationSettings>(System.apiURL.Admin + 'GetRegistrationSettings?yearId=' + this.yearService.defaultYear.Id);
    }

    getRegistrationStudentMatchOptions(): Observable<RegistrationStudentMatchOptions> {
        return this.http.get<RegistrationStudentMatchOptions>(System.apiURL.Admin + 'GetRegistrationStudentMatchOptions');
    }

    getRegistrationLookups(): Observable<RegistrationLookups> {
        return this.http.get<RegistrationLookups>(System.apiURL.Admin + 'GetRegistrationLookups?siteYearId=' + this.yearService.defaultYear.SiteYearId);
    }

    searchRegistration(searchCriteria: RegistrationSearchCriteria): Observable<RegistrationFamilySearch[]> {
        return this.http.post<RegistrationFamilySearch[]>(System.apiURL.Admin + 'SearchRegistration?yearId=' + this.yearService.defaultYear.Id, searchCriteria);
    }

    getRegistrationFamily(familyId: number): Observable<FamilyRegistrationDetails> {
        return this.http.get<FamilyRegistrationDetails>(System.apiURL.Admin + 'GetRegistrationFamily?yearId=' + this.yearService.defaultYear.SiteYearId + '&familyId=' + familyId);
    }

    saveFamilyRegistration(family: FamilyRegistrationDetails): Observable<FamilyRegistrationDetails> {
        return this.http.post<FamilyRegistrationDetails>(System.apiURL.Admin + 'SaveFamilyRegistration?yearId=' + this.yearService.defaultYear.SiteYearId, family);
    }

    deleteRegistrationHousehold(familyId: number, studentId: number): Observable<string> {
        return this.http.get<string>(System.apiURL.Admin + 'DeleteRegistrationHousehold?familyId=' + familyId + '&yearId=' + this.yearService.defaultYear.SiteYearId + '&studentId=' + studentId);
    }

    getAdditionalAddresses(familyId: number): Observable<FamilyAddress[]> {
        return this.http.get<FamilyAddress[]>(System.apiURL.Admin + 'GetAdditionalAddresses?familyId=' + familyId);
    }

    saveAdditionalAddress(familyId: number, transportation: boolean, deleteOperation: boolean, address: FamilyAddress): Observable<void> {
        return this.http.post<void>(System.apiURL.Admin + 'SaveAdditionalAddress?familyId=' + familyId + '&transportation=' + transportation + '&deleteOperation=' + deleteOperation, address);
    }

    getContactWorkInfo(familyId: number) {
        return this.http.get<ContactWorkInfo[]>(System.apiURL.Admin + 'GetContactWorkInfo?familyId=' + familyId);
    }

    getRegistrationDuplicateParentEmails(familyId: number, emailAdded: boolean, emailData: any): Observable<RegistrationDuplicateParentEmail[]> {
        return this.http.post<RegistrationDuplicateParentEmail[]>(System.apiURL.Admin + 'GetRegistrationDuplicateParentEmails?familyId=' + familyId + '&emailAdded=' + emailAdded, emailData);
    }

    getRegistrationDuplicatePhoneNumbers(familyId: number, phoneData: any): Observable<RegistrationDuplicatePhoneNumber[]> {
        return this.http.post<RegistrationDuplicatePhoneNumber[]>(System.apiURL.Admin + 'GetRegistrationDuplicatePhoneNumbers?familyId=' + familyId, phoneData);
    }

    changeDuplicateRegistrationEmails(emailData: any): Observable<void> {
        return this.http.post<void>(System.apiURL.Admin + 'ChangeDuplicateRegistrationEmails', emailData);
    }

    changeDuplicateRegistrationPhoneNumbers(phoneData: any): Observable<void> {
        return this.http.post<void>(System.apiURL.Admin + 'ChangeDuplicateRegistrationPhoneNumbers', phoneData);
    }

    getRegistrationPossibleDuplicateStudents(student: RegistrationStudent): Observable<RegistrationStudent[]> {
        return this.http.post<RegistrationStudent[]>(System.apiURL.Admin + 'GetRegistrationPossibleDuplicateStudents', student);
    }

    validateStudentIDs(studentId: number, studentIdentifier: string, stateId: string, lunchPIN: string, idLessThanPermission: boolean): Observable<string[]> {
        return this.http.get<string[]>(System.apiURL.Admin + 'ValidateStudentIDs?studentId=' + studentId + '&studentIdentifier=' + studentIdentifier + '&stateId=' + stateId + '&lunchPIN=' + lunchPIN + '&idLessThanPermission=' + idLessThanPermission);
    }

    saveQuickRegistration(schoolId: number, yearId: number, data: QuickRegistration): Observable<QuickRegistrationParent1Info> {
        return this.http.post<QuickRegistrationParent1Info>(System.apiURL.Admin + 'SaveQuickRegistration?schoolId=' + schoolId + '&yearId=' + yearId, data);
    }

    saveContactWorkInfo(workInfo: ContactWorkInfo, deleteOperation: boolean) {
        return this.http.post<void>(System.apiURL.Admin + 'SaveContactWorkInfo?delete=' + deleteOperation, workInfo);
    }

    getAddressChangeLogs(familyId: number): Observable<AddressChangeLog[]> {
        return this.http.get<AddressChangeLog[]>(System.apiURL.Admin + 'GetAddressChangeLogs?familyId=' + familyId);
    }

    getStudentReleaseAlerts(familyId: number): Observable<StudentReleaseAlert[]> {
        return this.http.get<StudentReleaseAlert[]>(System.apiURL.Admin + 'GetStudentReleaseAlerts?familyId=' + familyId);
    }

    saveStudentReleaseAlerts(releaseAlerts: StudentReleaseAlert[], deleteOperation: boolean) {
        return this.http.post<void>(System.apiURL.Admin + 'SaveStudentReleaseAlerts?delete=' + deleteOperation, releaseAlerts);
    }

    getStudentsLeaveWith(familyId: number): Observable<StudentLeavesWith[]> {
        return this.http.get<StudentLeavesWith[]>(System.apiURL.Admin + 'GetStudentsLeaveWith?familyId=' + familyId);
    }

    saveStudentsLeaveWith(leaveWithHousehold: boolean, leaveWithNonresident: boolean, studentsLeaveWith: StudentLeavesWith[]) {
        return this.http.post<void>(System.apiURL.Admin + 'SaveStudentsLeaveWith?familyId=' + this.familyRegistrationDetails.Family_ID + '&leaveWithHousehold=' + leaveWithHousehold + '&leaveWithNonresident=' + leaveWithNonresident, studentsLeaveWith);
    }

    saveNonresidentContacts(familyId: number, deleteOperation: boolean, contacts: FamilyContact[]): Observable<FamilyContact[]> {
        return this.http.post<FamilyContact[]>(System.apiURL.Admin + 'SaveNonresidentContacts?yearId=' + this.yearService.defaultYear.SiteYearId + '&familyId=' + familyId + '&delete=' + deleteOperation, contacts);
    }

    saveAdditionalContact(familyId: number, deleteOperation: boolean, contact: FamilyContact): Observable<FamilyContact[]> {
        return this.http.post<FamilyContact[]>(System.apiURL.Admin + 'SaveAdditionalContacts?familyId=' + familyId + '&delete=' + deleteOperation, contact);
    }

    saveHouseholdStudent(familyId: number, student: RegistrationStudent): Observable<RegistrationStudent[]> {
        return this.http.post<RegistrationStudent[]>(System.apiURL.Admin + 'SaveHouseholdStudent?siteYearId=' + this.yearService.defaultYear.SiteYearId + '&familyId=' + familyId, student);
    }

    saveStudentPhoto(studentId: number, base64Photo: string) {
        return this.http.post<void>(System.apiURL.Admin + 'SaveStudentPhoto?studentId=' + studentId, [base64Photo]);
    }

    getRegistrationAddressByStreetNameOrID(byName: boolean, streetName: string, streetId: number) {
        return this.http.get<FamilyAddress[]>(System.apiURL.Admin + 'GetRegistrationAddressByStreetNameOrID?byName=' + byName + '&streetName=' + streetName + '&streetId=' + streetId);
    }

    getAddressBlocks(block: string) {
        return this.http.get<AddressBlock[]>(System.apiURL.Admin + 'GetAddressBlocks?yearId=' + this.yearService.defaultYear.Id + '&block=' + block);
    }

    moveStudentsToFamily(familyId: number, studentIds: number[]) {
        return this.http.post<string>(System.apiURL.Admin + 'MoveStudentsToFamily?familyId=' + familyId, studentIds);
    }

    markStudentsAsNew(markNew: boolean, studentIds: number[]) {
        return this.http.post<string>(System.apiURL.Admin + 'MarkStudentsAsNew?yearId=' + this.yearService.defaultYear.Id + '&markNew=' + markNew, studentIds);
    }

    reconcileEdFiStudent(studentId: number, reconcile: boolean) {
        return this.http.get<void>(System.apiURL.Admin + 'ReconcileEdFiStudent?yearId=' + this.yearService.defaultYear.Id + '&studentId=' + studentId + '&reconcile=' + reconcile);
    }

    setEmergencyContactCallOrder(contacts: FamilyContact[]) {
        return this.http.post<void>(System.apiURL.Admin + 'SetEmergencyContactCallOrder', contacts);
    }

    sendNotificationEmailToParent(peopleId: number, updateDate: boolean, sendEmailInSpanish: boolean) {
        return this.http.get<void>(System.apiURL.Admin + 'SendNotificationEmailToParent?peopleId=' + peopleId + '&updateNotifyDate=' + updateDate + '&sendEmailInSpanish=' + sendEmailInSpanish);
    }

    deleteRegistrationContact(contact: FamilyContact) {
        return this.http.post<void>(System.apiURL.Admin + 'DeleteRegistrationContact', contact);
    }

    swapHouseholdParents(familyId: number, nonresident: boolean) {
        return this.http.get<FamilyContact[]>(System.apiURL.Admin + 'SwapHouseholdParents?familyId=' + familyId + '&siteYearId=' + this.yearService.defaultYear.SiteYearId + '&nonresident=' + nonresident);
    }

    getRegistrationEnrollments(schoolId: number, yearId: number, familyId: number, studentId: number, includeAllYearsEnrollment: boolean): Observable<StudentEnrollment[]> {
        let queryString = '';
        queryString += '?schoolId=' + schoolId +
            '&yearId=' + yearId +
            '&familyId=' + familyId +
            '&studentId=' + studentId +
            '&includeAllYearsEnrollment=' + includeAllYearsEnrollment;
        return this.http.get<StudentEnrollment[]>(System.apiURL.Admin + 'GetRegistrationEnrollments' + queryString);
    }

    splitStudentEnrollment(entryCodeId: number, studentEnrollment: StudentEnrollment) {
        let queryString = '';
        queryString += '?entryCodeId=' + entryCodeId;
        return this.http.post(System.apiURL.Admin + 'SplitStudentEnrollment' + queryString, studentEnrollment);
    }

    deleteStudentEnrollment(studentEnrollmentId: number, externalEnrollment: boolean): Observable<string> {
        let queryString = '';
        queryString += '?studentEnrollmentId=' + studentEnrollmentId +
            '&externalEnrollment=' + externalEnrollment;
        return this.http.get<string>(System.apiURL.Admin + 'DeleteStudentEnrollment' + queryString);
    }

    getRegistrationEnrollmentDetails(schoolId: number, yearId: number, studentId: number, studentEnrollmentId: number, externalEnrollment: boolean, newEnrollment: boolean, primaryEnrollments: boolean): Observable<StudentEnrollmentDetail[]> {
        let queryString = '';
        queryString += '?schoolId=' + schoolId +
            '&yearId=' + yearId +
            '&studentId=' + studentId +
            '&studentEnrollmentId=' + studentEnrollmentId +
            '&externalEnrollment=' + externalEnrollment +
            '&newEnrollment=' + newEnrollment +
            '&primaryEnrollments=' + primaryEnrollments;
        return this.http.get<StudentEnrollmentDetail[]>(System.apiURL.Admin + 'GetRegistrationEnrollmentDetails' + queryString);
    }

    getStudentCoursesByEnrollmentId(studentEnrollmentId: number): Observable<EnrollmentCourse[]> {
        let queryString = '';
        queryString += '?studentEnrollmentId=' + studentEnrollmentId;
        return this.http.get<EnrollmentCourse[]>(System.apiURL.Admin + 'GetStudentCoursesByEnrollmentId' + queryString);
    }

    updateWithdrawnPastCourses(studentEnrollmentId: number, siteSectionIds: string): Observable<void> {
        return this.http.get<void>(System.apiURL.Admin + 'UpdateWithdrawnPastCourses?studentEnrollmentId=' + studentEnrollmentId + '&siteSectionIds=' + siteSectionIds);
    }

    updatePastCourseDropped(courseAssignmentId: number, dropped: boolean): Observable<void> {
        return this.http.get<void>(System.apiURL.Admin + 'UpdatePastCourseDropped?courseAssignmentId=' + courseAssignmentId + '&dropped=' + dropped);
    }

    saveRegistrationEnrollmentDetail(screenName: string, enrollmentDetail: StudentEnrollmentDetail, selectedWithdrawalOptionIds: string): Observable<string> {
        let queryString = '';
        queryString += '?screenName=' + screenName;
        queryString += '&selectedWithdrawalOptionIds=' + selectedWithdrawalOptionIds;
        return this.http.post<string>(System.apiURL.Admin + 'SaveRegistrationEnrollmentDetail' + queryString, enrollmentDetail);
    }

    saveStudentExternalEnrollment(enrollmentDetail: StudentEnrollmentDetail): Observable<string> {
        return this.http.post<string>(System.apiURL.Admin + 'SaveStudentExternalEnrollment', enrollmentDetail);
    }

    getEnrollWithdrawDefaultCodes(statusChange: boolean) {
        return this.http.get<string>(System.apiURL.Admin + 'GetEnrollWithdrawDefaultCodes?statusChange=' + statusChange);
    }


    getStudentEnrollmentTotalFTE(schoolId: number, yearId: number, studentId: number, studentEnrollmentId: number, fte: number): Observable<number> {
        let queryString = '';
        queryString += '?schoolId=' + schoolId +
            '&yearId=' + yearId +
            '&studentId=' + studentId +
            '&studentEnrollmentId=' + studentEnrollmentId +
            '&fte=' + fte;
        return this.http.get<number>(System.apiURL.Admin + 'GetStudentEnrollmentTotalFTE' + queryString);
    }

    getHouseholdFamilyDocuments(familyId: number, blnShowAllDocuments: boolean): Observable<HouseholdFamilyDocument[]> {
        let queryString = '';
        queryString += '?familyId=' + familyId +
            '&blnShowAllDocuments=' + blnShowAllDocuments;
        return this.http.get<HouseholdFamilyDocument[]>(System.apiURL.Admin + 'GetHouseholdFamilyDocuments' + queryString);
    }

    deleteHouseholdFamilyDocumentById(documentId: number): Observable<boolean> {
        let queryString = '';
        queryString += '?documentId=' + documentId;
        return this.http.get<boolean>(System.apiURL.Admin + 'DeleteHouseholdFamilyDocumentById' + queryString);
    }

    saveHouseholdFamilyDocument(familyDocument: HouseholdFamilyDocument): Observable<boolean> {
        return this.http.post<boolean>(System.apiURL.Admin + 'SaveHouseholdFamilyDocument', familyDocument);
    }

    getEnrollmentHomeroomTeacherCourses(yearId: number, schoolId: number, studentId: number, staffId: number): Observable<EnrollmentHomeroomTeacher> {
        let queryString = '';
        queryString += '?yearId=' + yearId +
            '&siteId=' + schoolId +
            '&studentId=' + studentId +
            '&staffId=' + staffId;
        return this.http.get<EnrollmentHomeroomTeacher>(System.apiURL.Admin + 'GetEnrollmentHomeroomTeacherCourses' + queryString);
    }

    updateStudentCourseAssignmentFromEnrollment(yearId: number, enrollmentId: number, siteSectionIds: string, staffId: number, courseGroupId: number): Observable<number> {
        let queryString = '';
        queryString += '?yearId=' + yearId +
            '&studentEnrollmentId=' + enrollmentId +
            '&siteSectionIds=' + siteSectionIds +
            '&staffId=' + staffId +
            '&courseGroupId=' + courseGroupId;
        return this.http.get<number>(System.apiURL.Admin + 'UpdateStudentCourseAssignmentFromEnrollment' + queryString);
    }

    getHouseholdStudent(studentId: number): Observable<RegistrationStudent> {
        const queryString = '?studentId=' + studentId;
        return this.http.get<RegistrationStudent>(System.apiURL.Admin + 'GetHouseholdStudent' + queryString);
    }

    getEnrollmentCalendars(schoolId: number, yearId: number): Observable<EnrollmentCalendar[]> {
        let queryString = '';
        queryString += '?schoolId=' + schoolId +
            '&yearId=' + yearId;
        return this.http.get<EnrollmentCalendar[]>(System.apiURL.Admin + 'GetEnrollmentCalendars' + queryString);
    }

    showWarning(): Observable<boolean> | Promise<boolean> | boolean {
        // PRE: the user tries to perform an action which may need to be blocked
        // POST: Shows the warning dialog. Then, performs the following functions:
        // 		If 2, saves the family and continues and returns true
        // 		If 0, continues without saving the query and returns true
        // 		If -1, returns false

        if (this.unsavedChanges && this.maintainFamilyDataPermission) {
            this.observableGuard.next();
            const dialogBox = this.dialog.open(RegistrationUnsavedChangesDialogComponent, { panelClass: 'tyl-mat-dialog', disableClose: true, maxWidth: '450px' });
            dialogBox.componentInstance.validFamily = this.familyRegistrationDetails.Family_ID > 0;
            dialogBox.componentInstance.hasErrors = this.errorsExist;
            return dialogBox.afterClosed().pipe(
                map(result => {
                    switch (result) {
                        // Save query
                        case 2:
                            if (this.maintainFamilyDataPermission) {
                                this.saveFamilyRegistration(this.familyRegistrationDetails).subscribe(() => {
                                    return true;
                                });
                            }
                            break;
                        case 0:
                            return true;
                        // Cancel
                        case -1:
                            return false;
                        default:
                            return false;
                    }
                })
            );
        }
        else {
            return true;
        }
    }

    getProperMailingName() {
        let properMailingName = '';
        const prefixLookups = this.registrationLookups.PrefixData;

        const parent1 = this.familyRegistrationDetails.PrimaryContacts[0];
        const parent2 = this.familyRegistrationDetails.PrimaryContacts[1];
        const parent1Prefix = (parent1.Prefix && parent1.Prefix > 0) ? prefixLookups.filter(p => p.Id == parent1.Prefix.toString())[0].Code.trim() : '';
        const parent2Prefix = (parent2 && parent2.Prefix && parent2.Prefix > 0) ? prefixLookups.filter(p => p.Id == parent2.Prefix.toString())[0].Code.trim() : '';
        if (parent1Prefix.length > 0 && parent2Prefix.length > 0) {
            properMailingName = parent1Prefix + ' & ' + parent2Prefix;
            properMailingName += ' ' + parent1.LastName;
        }
        else if (parent2 && parent2.LastName && parent1.LastName.toUpperCase() == parent2.LastName.toUpperCase()) {
            properMailingName = parent1.FirstName.trim() + ' and ' + parent2.FirstName.trim() + ' ' + parent1.LastName.trim();
        }
        else if (parent2 && parent2.LastName && parent2.LastName.trim().length > 0) {
            properMailingName = parent1.FirstName.trim() + ' ' + parent1.LastName.trim() + ' and ' + parent2.FirstName.trim() + ' ' + parent2.LastName.trim();
        }
        else {
            if (parent1.Prefix && parent1.Prefix > 0) {
                properMailingName = parent1Prefix + ' ';
            }
            properMailingName += parent1.FirstName.trim() + ' ' + parent1.LastName.trim();
        }
        return properMailingName;
    }
}

export class RegistrationSearchParams {
    selectedTab: string;
    searchByStudent: SearchByStudentFields;
    searchByContact: SearchByContactFields;
    searchByAddress: SearchByAddressFields;
}

export class SearchByStudentFields {
    lastName: string;
    firstName: string;
    middleName: string;
    suffixId: number;
    stateId: number;
    everyoneLessThan: boolean;
    age: number;
    birthDate: string;
}

export class SearchByContactFields {
    lastName: string;
    firstName: string;
    email: string;
    phone: string;
    includeNonResidentParents: boolean;
    includeEmergencyContacts: boolean;
}

export class SearchByAddressFields {
    houseNumber: string;
    direction: number;
    streetName: string;
    streetType: number;
    aptLot: string;
    city: string;
    zipCode: string;
}
